/**
 * Radial action menu: appears while C is held, with a quick fade-and-expand
 * in and fade-and-collapse out (CSS `open` class). Live quadrants: CHARACTER
 * (left) opens appearance, outfits and stats. NAVIGATE (top) opens the
 * galactic chart; INVENTORY (right) opens the pack + fabricator. VEHICLES (bottom) opens ground/flying/space selection.
 *
 * Selection is by QUADRANT, not by button box: the cursor's angle from the
 * ring center arms a whole 90° sector (a center-out gradient wedge lights
 * up and the label brightens), and releasing C — or clicking — fires it.
 * A small deadzone at the center keeps "release on nothing" possible.
 */
export type VehicleCategory = "ground" | "flying" | "space";

export type ActionContext = "fly" | "foot";

type Quadrant = "left" | "right" | "top" | "bottom";

/** Cursor must be at least this far (px) from the center to arm a sector
 *  (matches the .am-center safe-area ring). */
const DEADZONE = 75;
/** Virtual cursor wander limit from the center (px). */
const RANGE = 320;

export class ActionMenu {
  private el: HTMLElement;
  private ring: HTMLElement;
  private hideTimer: number | null = null;
  private actions = new Map<string, () => void>();
  /** Sector layout for the current page. */
  private layout: Record<Quadrant, string | null> = {
    left: null,
    right: null,
    top: null,
    bottom: null,
  };
  /** Armed action + its quadrant (drives wedge/label highlight). */
  private hotAct: string | null = null;
  private hotQuad: Quadrant | null = null;
  visible = false;
  private vehiclePage = false;

  /**
   * VIRTUAL cursor, driven by raw mouse deltas: pointer lock stays HELD
   * while the wheel is open (no unlock races), and selection always starts
   * from the exact center of the ring.
   */
  private vx = 0;
  private vy = 0;
  private cursorEl!: HTMLElement;

  constructor(handlers: {
    onNavigate: () => void;
    /** Open the inventory + fabricator (right quadrant). */
    onInventory: () => void;
    /** Open CHARACTER — appearance, outfits and stats (left quadrant). */
    onYou: () => void;
    onVehicle: (category: VehicleCategory) => void;
    /** Fired whenever the wheel closes (main discards stale mouse deltas). */
    onClosed: () => void;
  }) {
    this.onClosed = handlers.onClosed;
    this.el = document.createElement("div");
    this.el.id = "action-menu";
    this.el.innerHTML = `
      <div class="am-ring">
        <div class="am-wedge am-w-left"></div>
        <div class="am-wedge am-w-right"></div>
        <div class="am-wedge am-w-top"></div>
        <div class="am-wedge am-w-bottom"></div>
        <div class="am-center"></div>
        <div id="am-cursor"></div>
        <div class="am-quad am-left" data-act="you"><span class="am-icon">◉</span>CHARACTER</div>
        <div class="am-quad am-right" data-act="inventory"><span class="am-icon">▦</span>INVENTORY</div>
        <div class="am-quad am-top" data-act="navigate"><span class="am-icon">◈</span>NAVIGATE</div>
        <div class="am-quad am-bottom" data-act="vehicles"><span class="am-icon">▣</span>VEHICLES</div>
      </div>
      <div class="am-hint">flick toward a quadrant and release C</div>
    `;
    this.el.style.display = "none";
    document.body.appendChild(this.el);
    this.ring = this.el.querySelector(".am-ring")!;

    this.actions.set("navigate", handlers.onNavigate);
    this.actions.set("inventory", handlers.onInventory);
    this.actions.set("you", handlers.onYou);
    for (const category of ["ground", "flying", "space"] as const) {
      this.actions.set(category, () => handlers.onVehicle(category));
    }

    this.cursorEl = this.el.querySelector("#am-cursor")!;

    // Document-level listeners: with pointer lock held, mouse events route
    // to the locked element (the canvas), never to this overlay.
    document.addEventListener("mousemove", (e) => {
      if (!this.visible) return;
      this.vx = Math.max(-RANGE, Math.min(RANGE, this.vx + e.movementX));
      this.vy = Math.max(-RANGE, Math.min(RANGE, this.vy + e.movementY));
      this.cursorEl.style.transform = `translate(${this.vx}px, ${this.vy}px) translate(-50%, -50%)`;
      this.track();
    });
    document.addEventListener("mousedown", (e) => {
      if (!this.visible || !this.hotAct) return;
      if (e.button === 0) this.release();
    });
  }

  private onClosed: () => void;

  private track(): void {
    const dx = this.vx;
    const dy = this.vy;
    let quad: Quadrant | null = null;
    if (Math.hypot(dx, dy) >= DEADZONE) {
      // 90° sectors around the axes.
      quad = Math.abs(dx) > Math.abs(dy) ? (dx > 0 ? "right" : "left") : dy > 0 ? "bottom" : "top";
    }
    const act = quad ? this.layout[quad] : null;
    this.setHot(act ? quad : null, act);
  }

  private setHot(quad: Quadrant | null, act: string | null): void {
    if (quad === this.hotQuad && act === this.hotAct) return;
    if (this.hotQuad) {
      this.ring.querySelector(`.am-w-${this.hotQuad}`)?.classList.remove("hot");
      for (const q of this.ring.querySelectorAll(`.am-quad.am-${this.hotQuad}`)) {
        q.classList.remove("hot");
      }
    }
    this.hotQuad = quad;
    this.hotAct = act;
    if (quad) {
      this.ring.querySelector(`.am-w-${quad}`)?.classList.add("hot");
      for (const q of this.ring.querySelectorAll(`.am-quad.am-${quad}`)) {
        if ((q as HTMLElement).style.display !== "none" && !q.classList.contains("am-soon")) {
          q.classList.add("hot");
        }
      }
    }
  }

  /**
   * Release-to-select: if the cursor sits in an armed sector when C is
   * released, fire it. Submenus stay open; returns whether something fired.
   */
  release(): boolean {
    const act = this.hotAct;
    if (!act && this.vehiclePage) return false;
    if (act === "vehicles" || act === "back") {
      this.setPage(act === "vehicles");
      return true;
    }
    this.hide();
    if (act) {
      this.actions.get(act)?.();
      return true;
    }
    return false;
  }

  show(ctx: ActionContext): void {
    if (this.hideTimer !== null) {
      clearTimeout(this.hideTimer);
      this.hideTimer = null;
    }
    // Both modes expose the same actions; summon handlers explain refusals.
    void ctx;
    this.setPage(false);
    // The virtual cursor ALWAYS starts dead-center — flick from zero.
    this.vx = 0;
    this.vy = 0;
    this.cursorEl.style.transform = "translate(0px, 0px) translate(-50%, -50%)";
    this.setHot(null, null);
    this.visible = true;
    this.el.style.display = "flex";
    void this.el.offsetWidth; // commit display before the transition starts
    this.el.classList.add("open");
  }

  private setPage(vehicles: boolean): void {
    this.vehiclePage = vehicles;
    this.setHot(null, null);
    this.layout = vehicles
      ? { left: "ground", top: "flying", right: "space", bottom: "back" }
      : { left: "you", right: "inventory", top: "navigate", bottom: "vehicles" };
    const icons: Record<string, string> = { ground: "▰", flying: "◇", space: "✦", back: "↩", navigate: "◈", inventory: "▦", you: "◉", vehicles: "▣" };
    for (const quadrant of ["left", "right", "top", "bottom"] as const) {
      const act = this.layout[quadrant]!;
      const label = this.ring.querySelector<HTMLElement>(`.am-quad.am-${quadrant}`)!;
      label.dataset.act = act;
      label.innerHTML = `<span class="am-icon">${icons[act]}</span>${act === "you" ? "CHARACTER" : act.toUpperCase()}`;
    }
    this.vx = this.vy = 0;
    this.cursorEl.style.transform = "translate(0px, 0px) translate(-50%, -50%)";
    this.el.querySelector(".am-hint")!.textContent = vehicles
      ? "choose a vehicle type · click or press and release C · esc cancels"
      : "flick toward a quadrant and release C";
  }

  hide(): void {
    if (!this.visible && this.hideTimer === null) return;
    this.visible = false;
    this.setHot(null, null);
    this.onClosed();
    this.el.classList.remove("open"); // plays the collapse...
    this.hideTimer ??= window.setTimeout(() => {
      this.el.style.display = "none"; // ...then actually leaves the DOM flow
      this.hideTimer = null;
    }, 140);
  }
}
